import { telegram } from './telegram';

/**
 * Светлая и тёмная тема.
 *
 * По умолчанию берём ту, что выбрана в Telegram: женщина уже сделала этот
 * выбор один раз, и переспрашивать её незачем. Если она выбрала явно —
 * помним выбор и Telegram больше не слушаем.
 *
 * Системную настройку браузера учитываем только вне Telegram: внутри
 * приложения `prefers-color-scheme` не отражает тему клиента и врёт.
 */

export type ThemeChoice = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'lma:theme';

/** Цвет шапки Telegram обязан совпадать с фоном, иначе видна чужая полоса. */
const CHROME: Record<'light' | 'dark', string> = {
  dark: '#14101F',
  light: '#FAF8FC',
};

export function readChoice(): ThemeChoice {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    // Приватный режим или запрет на хранилище — тема просто не запомнится.
    return 'system';
  }
}

export function saveChoice(choice: ThemeChoice): void {
  try {
    if (choice === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    // Не запомнить выбор неприятно, но не повод ломать экран.
  }
}

/** Какая тема получается из выбора и окружения. */
export function resolveTheme(choice: ThemeChoice): 'light' | 'dark' {
  if (choice !== 'system') return choice;

  const app = telegram();
  if (app?.colorScheme) return app.colorScheme;

  return typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: light)').matches
    ? 'light'
    : 'dark';
}

/** Применить тему к странице и к рамке Telegram вокруг неё. */
export function applyTheme(choice: ThemeChoice): 'light' | 'dark' {
  const resolved = resolveTheme(choice);

  document.documentElement.dataset.theme = resolved;
  document.documentElement.style.colorScheme = resolved;

  const app = telegram();
  app?.setHeaderColor(CHROME[resolved]);
  app?.setBackgroundColor(CHROME[resolved]);

  return resolved;
}
